<script>
    import { onMount, tick } from "svelte";

    import { apiMethods } from "../../../Services/index.svelte";

    import {
        getDateFromDateString,
        isObjectEmpty,
        showError,
    } from "../../../Utils/utilFunctions.svelte";

    import {
        CardLayout,
        Loader,
        NotificationModal,
        PageLoading,
        SvelteTable,
    } from "../../../Components/Common/index.svelte";
    import {
        loadEmailTypes,
        loadSMSTypes,
    } from "../../../Utils/DataLoaders.svelte";

    import { EmailTypes, SMSTypes, user } from "../../../stores";
    import { checkEnvIsGroup } from "../../../Utils/utilRoute.svelte";

    export let encounterId = Number(0);
    export let patientEncounter;
    export let reloadPatientEncounter = () => {};
    export let loadEncounterStatusOfPatient = () => {};
    export let emailNotifications = [],
        smsNotifications = [],
        commsLoaded = false,
        loadCommunicationData = async () => {};

    export let questionnaireAnswer,
        hasQuestionnaireAnswerLoaded = false;

    let isGettingQuestionnaireAnswer = false;

    let isLoadingEncounterStatusOfPatient = false,
        showCopyPreOpQuestionnaireIcon = false,
        encounterIdCopyPreOpQuestionnaire,
        isCopyingPreOpQuestionnaire = false,
        showConfirmCopyPreOpQuestionnaireModal = false;

    let sendQuestionnaireToPatientNowModalOpen = false,
        isSendingQuestionnaireToPatientNow = false,
        sendQuestionnaireToPatientSuccessModalOpen = false;

    let noQuestionnaireAnswerText =
            "The patient didn't complete the questionnaire yet.",
        checkingNoAnswerText = false;
    
    $: isGroupEnv = checkEnvIsGroup($user.envSettings);

    $: btnOptions = [
        {
            iconName: "sendPreOpQuestionnaireToPatient",
            iFont: "fa-solid fa-paper-plane",
            btnText: "Send the questionnaire to the patient now",
            isHidden: questionnaireAnswer,
            iconFunction: () => _openSendQuestionnaireToPatientNowModal(),
        },
    ];

    const columns = [
        {
            key: "question",
            title: "Question",
            headerClass: "text-left pl-3 cursor-default w-[30%] rounded-tl-xl",
            class: "text-left px-3 cursor-default w-[30%]",
            renderValue: (v) => v.title,
        },
        {
            key: "answer",
            title: "Answer",
            headerClass: "text-left pl-3 cursor-default w-[70%] rounded-tr-xl",
            class: "text-left px-3 cursor-default w-[70%]",
            renderValue: (v) =>
                v.value
                    ? v.value.toString().toUpperCase().includes("ERROR")
                        ? ""
                        : v.value
                    : "",
        },
    ];

    let doctorGroupDetails;

    onMount(async () => {
        
        !hasQuestionnaireAnswerLoaded && (await getQuestionnaireAnswer());

        if (!questionnaireAnswer) {
            await loadEmailTypes();
            await loadSMSTypes();

            await tick();

            await determineNoAnswerText();

            await triggerLoadEncounterStatusOfPatient();
        }

        if(patientEncounter.doctor?.groupId) {
            //load doctor group details
            try {
                var groupDetails = await apiMethods.getMethod.getGroupById(patientEncounter.doctor?.groupId);
                if(!groupDetails.error) {
                    doctorGroupDetails = groupDetails;
                }
            }
            catch(e) {
                console.log("Error getting group details:", e);
            }
            
        }
    });

    const getQuestionnaireAnswer = async () => {
        isGettingQuestionnaireAnswer = true;

        await apiMethods.getMethod
            .getEncounterPreOpQuestionnaireAnswer(encounterId)
            .then((res) => {
                questionnaireAnswer = res;
                hasQuestionnaireAnswerLoaded = true;
            })
            .catch((err) => showError(err));

        isGettingQuestionnaireAnswer = false;
    };

    const determineNoAnswerText = async () => {
        checkingNoAnswerText = true;

        if (
            !emailNotifications?.length &&
            !smsNotifications?.length &&
            !commsLoaded
        )
            await loadCommunicationData();

        if (emailNotifications?.length)
            emailNotifications = emailNotifications.map((ntf) => {
                return {
                    ...ntf,
                    typeName:
                        $EmailTypes.find((et) => et.id == ntf.type)?.type ??
                        "Missing Type Name",
                };
            });

        if (smsNotifications?.length)
            smsNotifications = smsNotifications.map((ntf) => {
                return {
                    ...ntf,
                    typeName:
                        $SMSTypes.find((et) => et.id == ntf.type)?.type ??
                        "Missing Type Name",
                };
            });

        let displayDate = "";

        // HERE check "Pre Op Questionnaire"
        const filteredPreOpNotifications = [
            ...emailNotifications.filter((ntf) =>
                ntf.typeName.includes("Pre Op Questionnaire"),
            ),
            ...smsNotifications.filter((ntf) =>
                ntf.typeName.includes("Pre Op Questionnaire"),
            ),
        ];

        if (filteredPreOpNotifications.length) {
            const earliestNtf = filteredPreOpNotifications.reduce(
                (a, b) =>
                    new Date(a.dateToSend) < new Date(b.dateToSend) ? a : b,
                filteredPreOpNotifications[0],
            );

            displayDate = earliestNtf.dateToSend;
        } else {
            // HERE check "Estimate"
            const filteredEstimateNotifications = [
                ...emailNotifications.filter((ntf) =>
                    ntf.typeName.includes("Estimate"),
                ),
                ...smsNotifications.filter((ntf) =>
                    ntf.typeName.includes("Estimate"),
                ),
            ];

            if (filteredEstimateNotifications.length) {
                const earliestNtf = filteredEstimateNotifications.reduce(
                    (a, b) =>
                        new Date(a.dateToSend) < new Date(b.dateToSend) ? a : b,
                    filteredEstimateNotifications[0],
                );

                displayDate = earliestNtf.dateToSend;
            }
        }

        if (displayDate)
            noQuestionnaireAnswerText = `The survey was first send to the patient on ${getDateFromDateString(displayDate)} to complete. The patient didn't complete the questionnaire yet.`;

        checkingNoAnswerText = false;
    };

    const triggerLoadEncounterStatusOfPatient = async () => {
        if (isLoadingEncounterStatusOfPatient) return;

        isLoadingEncounterStatusOfPatient = true;

        const res = await loadEncounterStatusOfPatient();

        if (res.error) showError(res.error);
        else
            showCopyPreOpQuestionnaireIcon =
                _determineShowCopyPreOpQuestionnaireIcon(res.results);

        isLoadingEncounterStatusOfPatient = false;
    };

    const handleCopyPreOpQuestionnaire = async () => {
        if (isCopyingPreOpQuestionnaire || !encounterIdCopyPreOpQuestionnaire)
            return;

        isCopyingPreOpQuestionnaire = true;

        await apiMethods.putMethod
            .copyPreOpQuestionnaire(
                encounterIdCopyPreOpQuestionnaire,
                encounterId,
            )
            .then(async (res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to copy Pre-Op Questionnaire answer to this encounter, please refresh the page and try again and contact support if the issue persists.",
                    );
                else {
                    await getQuestionnaireAnswer();
                    await reloadPatientEncounter();
                }
            });

        isCopyingPreOpQuestionnaire = false;

        _closeConfirmCopyPreOpQuestionnaireModal();
    };

    const sendPreOpQuestionnaireToPatientNow = async () => {
        isSendingQuestionnaireToPatientNow = true;

        await apiMethods.postMethod
            .sendPreOpQuestionnaire(encounterId)
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to send PreOp Questionnaire to the patient, please refresh the page and try again and contact support if the issue persists.",
                    );
                else _openSendQuestionnaireToPatientSuccessModal();
            });

        isSendingQuestionnaireToPatientNow = false;

        _closeSendQuestionnaireToPatientNowModal();
    };

    // HELPER FUNCTIONS
    const _openSendQuestionnaireToPatientNowModal = () =>
        (sendQuestionnaireToPatientNowModalOpen = true);

    const _closeSendQuestionnaireToPatientNowModal = () =>
        (sendQuestionnaireToPatientNowModalOpen = false);

    const _openSendQuestionnaireToPatientSuccessModal = () =>
        (sendQuestionnaireToPatientSuccessModalOpen = true);

    const _closeSendQuestionnaireToPatientSuccessModal = () =>
        (sendQuestionnaireToPatientSuccessModalOpen = false);

    const _determineShowCopyPreOpQuestionnaireIcon = (encounters = []) => {
        const { preOpQuestionnaireId, hasPreOpQuestionnaireAnswers } =
            patientEncounter;

        if (
            !preOpQuestionnaireId ||
            hasPreOpQuestionnaireAnswers ||
            encounters?.length <= 1
        )
            return false;

        const targetEncounter = encounters.find(
            (encounter) =>
                encounter.preOpQuestionnaireId > 0 &&
                !!encounter.hasPreOpQuestionnaireAnswers,
        );

        if (!targetEncounter || isObjectEmpty(targetEncounter)) return false;

        encounterIdCopyPreOpQuestionnaire = targetEncounter.id;

        return true;
    };

    const _openConfirmCopyPreOpQuestionnaireModal = () =>
        (showConfirmCopyPreOpQuestionnaireModal = true);

    const _closeConfirmCopyPreOpQuestionnaireModal = () =>
        (showConfirmCopyPreOpQuestionnaireModal = false);

</script>

<div
    class="relative flex mt-4 {!isGettingQuestionnaireAnswer && questionnaireAnswer
        ? 'flex-1'
        : ''}"
>
    <CardLayout
        title="PreOp Questionnaire Answer"
        enableCollapse={false}
        {btnOptions}
    >
        {#if isGettingQuestionnaireAnswer}
            <div class="flex flex-1 w-full items-center justify-center py-4">
                <PageLoading />
            </div>
        {:else if !questionnaireAnswer}
            <div class="flex flex-col w-full py-4 px-2 gap-y-2">
                {#if checkingNoAnswerText}
                    <div class="flex items-center gap-x-2">
                        <Loader />
                        <p>Checking answers...</p>
                    </div>
                {:else}
                    <p>{noQuestionnaireAnswerText}</p>
                {/if}
                {#if showCopyPreOpQuestionnaireIcon}
                    <!-- svelte-ignore a11y-click-events-have-key-events -->
                    <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
                    <p
                        class="underline {isCopyingPreOpQuestionnaire
                            ? 'cursor-not-allowed'
                            : 'cursor-pointer font-bold'}"
                        on:click={_openConfirmCopyPreOpQuestionnaireModal}
                    >
                        Copy answers from a previous encounter
                    </p>
                {/if}
            </div>
        {:else}
            <div
                class="flex flex-col gap-y-2 absolute inset-0 overflow-y-auto p-4"
            >
                <div class="flex flex-col flex-1 gap-y-4 relative">
                    <p>
                        The patient has completed the questionnaire at {questionnaireAnswer.created_at}
                    </p>

                    <div class="flex absolute inset-0 overflow-y-auto mt-8">
                        <SvelteTable
                            {columns}
                            rows={questionnaireAnswer.data.filter(
                                ({ type, title, value }) =>
                                    type != "hidden" &&
                                    title &&
                                    !/encounter/.test(title) &&
                                    !/^show\w+/.test(title) &&
                                    value &&
                                    !value
                                        .toString()
                                        .toLowerCase()
                                        .includes("error"),
                            )}
                        />
                    </div>
                </div>
            </div>
        {/if}
    </CardLayout>
</div>

<NotificationModal
    isModalOpen={sendQuestionnaireToPatientNowModalOpen}
    title="Send the PreOp Questionnaire to the patient now?"
    cancelText="Cancel"
    submitText={isSendingQuestionnaireToPatientNow ? "Sending" : "Send Now"}
    isConfirming={isSendingQuestionnaireToPatientNow}
    closeModal={_closeSendQuestionnaireToPatientNowModal}
    confirmAction={sendPreOpQuestionnaireToPatientNow}
    closeModalFunction={_closeSendQuestionnaireToPatientNowModal}
    modalMaxWidth="max-w-3xl"
>
    {#if isGroupEnv && doctorGroupDetails != null}
        <p class="leading-6">
            The PreOp Questionnaire notification is usually sent to the patient {doctorGroupDetails.preOpQuestionnaireNotifyDays} days
            before the procedure date.
        </p>
    {:else}
        <p class="leading-6">
            The PreOp Questionnaire notification is usually sent to the patient 72h
            before the procedure date.
        </p>
    {/if}
    
    <p class="leading-6">Do you want to send the notification now?</p>
</NotificationModal>

<NotificationModal
    title="Done"
    isModalOpen={sendQuestionnaireToPatientSuccessModalOpen}
    showCancelBtn={false}
    submitText="Close"
    closeModalFunction={_closeSendQuestionnaireToPatientSuccessModal}
    confirmAction={_closeSendQuestionnaireToPatientSuccessModal}
>
    The PreOp Questionnaire was sent to the patient.
</NotificationModal>

<NotificationModal
    isModalOpen={showConfirmCopyPreOpQuestionnaireModal}
    title="Copy past questionnaire answers?"
    cancelText="Cancel"
    closeModal={_closeConfirmCopyPreOpQuestionnaireModal}
    closeModalFunction={_closeConfirmCopyPreOpQuestionnaireModal}
    submitText={isCopyingPreOpQuestionnaire ? "Processing" : "Confirm"}
    confirmAction={handleCopyPreOpQuestionnaire}
    isConfirming={isCopyingPreOpQuestionnaire}
>
    <p class="leading-6 p-4">
        Copy the patient pre-op questionnaire answers from their last encounter?
    </p>
</NotificationModal>
